/**
 * Ids dos sons do jogo original (`SoundManager.instance.play("008")`). Os efeitos são `audio/sfx/<id>.mp3` (extraídos do audio.swf) e as
 * músicas são `audio/<id>.mp3` (Resource/sound/<id>.flv). Cada id abaixo aponta o código do cliente antigo que o toca.
 */
export const SFX = {
  /** Qualquer botão da interface (904 usos no cliente antigo). */
  click: '008',
  /** Mira subiu ou desceu (ArrowView). */
  angle: '006',
  /** Barra de força enchendo, em laço (EnergyView). */
  charge: '020',
  /** Soltou a barra de força: o tiro sai (EnergyView). */
  release: '019',
  /** Andando (GamePlayer/TrainerWalkAction). */
  walk: '044',
  /** Um segundo do turno, enquanto sobra tempo (CountDownView). */
  turnTick: '014',
  /** Últimos 4 segundos do turno (CountDownView). */
  turnWarning: '067',
  /** Jogador morreu (GamePlayer.die). */
  die: '042',
  /** Fim de partida: vitória / derrota (ExpView). */
  win: '063',
  lose: '064',
  /**
   * Par padrão de disparo e explosão, para a bomba que não está em `ball-sounds.ts`. No original cada projétil traz o seu
   * (BallInfo.ShootSound / BombSound), e a partida usa `shootSoundOf` e `bombSoundOf`.
   */
  shoot: '099',
  bomb: '098',
} as const;

export const MUSIC = {
  /** Lobby, lista de salas e telas gerais (playMusic("062")). */
  lobby: '062',
  /** Dentro da sala, antes da partida (BaseRoomState). */
  room: '065',
  /** Mapa sem trilha própria: o editor de mapas do original usa 1001 como padrão de BackMusic. */
  defaultMap: '1001',
} as const;

/** Categorias do mixer de som: cada uma tem o próprio volume e botão de mudo. */
export type SoundCategory = 'music' | 'combat' | 'ui' | 'movement' | 'turn';

export interface SoundCategoryInfo {
  id: SoundCategory;
  label: string;
  /** Quais sons a categoria controla, para o jogador entender o que está baixando. */
  hint: string;
}

export const SOUND_CATEGORIES: readonly SoundCategoryInfo[] = [
  { id: 'music', label: 'Música', hint: 'Trilhas do lobby, da sala e dos mapas' },
  { id: 'combat', label: 'Combate', hint: 'Disparos, explosões e mortes' },
  { id: 'ui', label: 'Interface', hint: 'Cliques de botão e ajuste da mira' },
  { id: 'movement', label: 'Movimento', hint: 'Andar, carregar e soltar a força' },
  { id: 'turn', label: 'Turno e fim', hint: 'Contagem do turno, alerta e vitória ou derrota' },
];

/** Em qual categoria cada efeito entra. Efeito fora da tabela cai em Combate. */
export const SFX_CATEGORY: Readonly<Record<string, SoundCategory>> = {
  [SFX.click]: 'ui',
  [SFX.angle]: 'ui',
  [SFX.charge]: 'movement',
  [SFX.release]: 'movement',
  [SFX.walk]: 'movement',
  [SFX.turnTick]: 'turn',
  [SFX.turnWarning]: 'turn',
  [SFX.win]: 'turn',
  [SFX.lose]: 'turn',
  [SFX.die]: 'combat',
  [SFX.shoot]: 'combat',
  [SFX.bomb]: 'combat',
};

/** Som de exemplo tocado ao soltar a barra de uma categoria de efeitos (música não tem: ela já está tocando). */
export const SFX_PREVIEW: Readonly<Partial<Record<SoundCategory, string>>> = {
  combat: SFX.bomb,
  ui: SFX.click,
  movement: SFX.release,
  turn: SFX.turnTick,
};
